<template>
  <div class="gantt-page ma-4">
    <div class="d-flex align-center mb-4">
      <v-btn
        icon
        @click="$router.back()">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>
      <h2 class="ml-2 mr-2">
        Gantt Chart
      </h2>
      <router-link
        v-if="prototype"
        class="text-bold"
        :to="prototype.state === 'sketch'
          ? `/prototype/sketch/edit/${prototype.id}`
          : `/prototype/order/edit/${prototype.id}`"
        target="_blank"
      >
        {{ prototype.sketch.sketchId }}
      </router-link>
      <v-spacer></v-spacer>
      <v-btn
        v-if="prototype && isControllerUser"
        outlined
        class="mr-2"
        @click="openPrototypeHistory">
        <v-icon
          left
          small>
          mdi-history
        </v-icon>
        ประวัติการแก้ไข
      </v-btn>
      <v-btn
        v-if="isControllerUser"
        color="primary"
        :disabled="!isDirty || saving"
        @click="openSaveDialog">
        SAVE
      </v-btn>
    </div>
    <div class="d-flex align-center mb-4">
      <span class="font-weight-medium mr-2">
        {{ collectionName }} - {{ salesDate | salesDateInLocalTimeZone }}
      </span>
      <span class="text-caption"> “Dates follow your local device timezone.”</span>
    </div>

    <div
      v-if="loading"
      class="pa-4 text-center">
      <v-progress-circular
        indeterminate
        size="24" />
    </div>
    <div
      v-else-if="!workflow"
      class="pa-4 text-center">
      กรุณาสร้าง workflow ก่อนดำเนินการต่อ
    </div>
    <div
      v-else-if="!prototype"
      class="pa-4 text-center">
      ไม่พบ Sketch นี้
    </div>
    <v-card
      v-else
      elevation="3"
      class="pa-2">
      <div class="chart">
        <div class="grid">
          <div class="names">
            <div class="head">
              <div class="head-month-label"></div>
              <div class="head-stage-label">
                Stage
              </div>
            </div>
            <div
              v-for="row in rows"
              :key="row.stage.id"
              class="cell">
              <div class="cell-text">
                <span class="t">{{ row.stage.name }}</span>
                <span
                  v-if="row.task"
                  class="d">
                  {{ formatDay(row.startDay) }} – {{ formatDay(row.startDay + row.duration - 1) }} · {{ row.duration }} วัน
                </span>
                <span
                  v-else
                  class="d grey--text">
                  No Task
                </span>
              </div>
              <v-tooltip
                v-if="row.task && isControllerUser"
                top>
                <template #activator="{ on, attrs }">
                  <v-btn
                    icon
                    x-small
                    v-bind="attrs"
                    v-on="on"
                    @click="openTaskHistory(row)">
                    <v-icon
                      small
                      :color="row.task.changeDateBy ? 'primary' : ''">
                      mdi-history
                    </v-icon>
                  </v-btn>
                </template>
                <span>{{ lastChangedText(row.task) }}</span>
              </v-tooltip>
            </div>
          </div>

          <div
            ref="scrollEl"
            class="scroll">
            <div
              class="timeline"
              :style="{ width: totalDays * dayW + 'px' }">
              <div class="months-row">
                <div
                  v-for="m in months"
                  :key="m.key"
                  class="month-cell"
                  :style="{ left: m.left + 'px', width: (m.count * dayW) + 'px', background: m.bg, color: m.textColor }">
                  {{ m.label }}
                </div>
              </div>
              <div class="days">
                <div
                  v-for="c in columns"
                  :key="'h' + c.abs"
                  class="day"
                  :class="{ weekend: c.weekend, [c.dayOffClass]: !!c.dayOffClass }"
                  :title="c.dayOffTitle">
                  <span>{{ c.wd }}</span>
                  <span class="num">{{ c.num }}</span>
                </div>
              </div>

              <div class="rows">
                <div class="colbg">
                  <div
                    v-for="c in columns"
                    :key="'c' + c.abs"
                    class="c"
                    :class="{ weekend: c.weekend, [c.dayOffClass]: !!c.dayOffClass }"
                    :style="{ left: c.left + 'px', width: dayW + 'px' }"
                    :title="c.dayOffTitle"></div>
                </div>
                <div
                  v-if="todayVisible"
                  class="today-line"
                  :style="{ left: todayLeft + 'px' }">
                  <span class="today-flag">Today</span>
                </div>
                <div
                  v-if="saleDateVisible"
                  class="saleDate-line"
                  :style="{ left: saleDateLeft + 'px' }">
                  <span class="saleDate-flag">Sale Date</span>
                </div>

                <div
                  v-for="row in rows"
                  :key="row.stage.id"
                  class="gantt-row">
                  <div
                    v-if="row.task"
                    class="bar"
                    :class="[
                      statusStyleFor(row).bgClass,
                      statusStyleFor(row).textColor1,
                      {
                        'bar--locked': row.locked,
                        'bar--selected': selectedKeys.includes(row.stage.id),
                        dragging: isDragging(row)
                      }
                    ]"
                    :style="barStyle(row)"
                    @pointerdown="startMove($event, row)"
                    @contextmenu.prevent>
                    <div
                      v-if="!row.locked"
                      class="handle l"
                      @pointerdown.stop="startResize($event, row, 'l')"></div>
                    <span class="txt">{{ row.stage.name }}</span>
                    <v-icon
                      v-if="row.locked"
                      x-small
                      class="ml-1">
                      mdi-lock
                    </v-icon>
                    <div
                      v-if="!row.locked"
                      class="handle r"
                      @pointerdown.stop="startResize($event, row, 'r')"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <p class="hint">
        <b>ลากกลางแถบ</b> = เลื่อนวันเริ่ม/สิ้นสุดพร้อมกัน (จำนวนวันเท่าเดิม) ·
        <b>ลากขอบซ้าย/ขวา</b> = เพิ่ม–ลดจำนวนวัน ·
        <b>⌘/Ctrl + คลิก</b> = เลือกหลาย bar เพื่อลากพร้อมกัน (Esc = ยกเลิกการเลือก) ·
        <v-icon x-small>
          mdi-lock
        </v-icon> = Task ที่ completed แล้ว ล็อกไว้ ลากไม่ได้ ·
        <span class="legend-swatch legend-th"></span> วันหยุดไทย ·
        <span class="legend-swatch legend-cn"></span> วันหยุดจีน ·
        <span class="legend-swatch legend-both"></span> วันหยุดทั้งไทยและจีน
      </p>
    </v-card>

    <v-dialog
      v-model="noteDialog"
      max-width="420"
      :persistent="false">
      <v-card>
        <v-card-title class="headline">
          บันทึกการเปลี่ยนแปลง
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="note"
            label="Note"
            rows="3"
            auto-grow
            outlined
            placeholder="Add a note"
            :rules="[v => !!v || 'กรุณาระบุโน๊ต']"
          />
        </v-card-text>
        <v-divider />
        <v-card-actions>
          <v-spacer />
          <v-btn
            text
            @click="noteDialog = false">
            Cancel
          </v-btn>
          <v-btn
            color="primary"
            :disabled="!note || saving"
            :loading="saving"
            @click="confirmSave">
            Confirm
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <task-history-drawer
      v-if="isControllerUser"
      v-model="historyDrawer"
      :task-id="historyTaskId"
      :prototype-id="prototype ? prototype.id : null"
      :stages="workflow ? workflow.stages : []"
      :title="historyTitle" />
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import ProductionPlanProvider from '@/resources/ProductionPlanProvider'
import calculateTaskStatus from '@/assets/js/CalculateTaskStatus'
import TASK_STATUS_STYLES from '@/assets/js/TaskStatusStyles'
import { getRole } from '@/assets/js/Authentication'
import ProductCollectionProvider from '@/resources/ProductCollectionProvider'
import TaskHistoryDrawer from '../components/TaskHistoryDrawer.vue'

const productionPlanService = new ProductionPlanProvider()
const productCollectionService = new ProductCollectionProvider()

const LEAD_DAYS = 10
const DAY_WIDTH = 44
const WD_TH = ['อา', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส']
const MONTH_TH = ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
const MONTH_COLORS = [
  { bg: '#e3f2fd', text: '#1565c0' },
  { bg: '#fce4ec', text: '#ad1457' },
  { bg: '#e8f5e9', text: '#2e7d32' },
  { bg: '#fff8e1', text: '#f57f17' },
  { bg: '#e0f2f1', text: '#00695c' },
  { bg: '#fff3e0', text: '#e65100' },
  { bg: '#e8eaf6', text: '#283593' },
  { bg: '#f9fbe7', text: '#558b2f' },
  { bg: '#e0f7fa', text: '#006064' },
  { bg: '#fbe9e7', text: '#bf360c' },
  { bg: '#efebe9', text: '#4e342e' },
  { bg: '#f3e5f5', text: '#6a1b9a' }
]

export default {
  components: {
    TaskHistoryDrawer
  },
  data () {
    return {
      loading: false,
      saving: false,
      workflow: null,
      prototype: null,
      rows: [],
      anchor: null,
      dayW: DAY_WIDTH,
      drag: null,
      compBase: null,
      selectedKeys: [],
      noteDialog: false,
      note: '',
      userRole: null,
      dayOffs: [],
      salesDate: null,
      collectionName: null,
      historyDrawer: false,
      historyTaskId: null,
      historyTitle: ''
    }
  },
  computed: {
    isControllerUser () {
      return ['merchandising_planner', 'developer', 'management'].includes(this.userRole)
    },
    viewStart () {
      const withBars = this.rows.filter((row) => row.task)
      if (!withBars.length) return -LEAD_DAYS
      return Math.min(...withBars.map((row) => row.startDay)) - LEAD_DAYS
    },
    viewEnd () {
      const withBars = this.rows.filter((row) => row.task)
      const barsEnd = withBars.length
        ? Math.max(...withBars.map((row) => row.startDay + row.duration))
        : 0
      const saleDayEnd = this.saleDateDay ?? 0
      return Math.max(barsEnd, saleDayEnd) + LEAD_DAYS
    },
    totalDays () {
      return this.viewEnd - this.viewStart
    },
    dayOffMap () {
      const map = {}
      this.dayOffs.forEach((item) => {
        const abs = this.dateToDay(item.dayOffDate)
        if (!map[abs]) map[abs] = { scopes: new Set(), names: [] }
        map[abs].scopes.add(item.factoryScope)
        map[abs].names.push(item.name)
      })

      Object.keys(map).forEach((abs) => {
        const { scopes } = map[abs]
        const hasAll = scopes.has('all')
        const hasTh = hasAll || scopes.has('thailand')
        const hasCn = hasAll || scopes.has('china')
        if (hasTh && hasCn) map[abs].cssClass = 'dayoff-both'
        else if (hasTh) map[abs].cssClass = 'dayoff-th'
        else if (hasCn) map[abs].cssClass = 'dayoff-cn'
        else map[abs].cssClass = ''
      })

      return map
    },
    months () {
      const out = []
      let cur = null
      this.columns.forEach((c) => {
        const d = this.dayToDate(c.abs)
        const key = `${d.year()}-${d.month()}`
        if (!cur || cur.key !== key) {
          cur = {
            key,
            label: `${MONTH_TH[d.month()]} ${d.year()}`,
            count: 1,
            left: c.left,
            bg: MONTH_COLORS[d.month()].bg,
            textColor: MONTH_COLORS[d.month()].text
          }
          out.push(cur)
        } else {
          cur.count++
        }
      })
      return out
    },
    columns () {
      const out = []
      for (let i = 0; i < this.totalDays; i++) {
        const abs = this.viewStart + i
        const d = this.dayToDate(abs)
        const wd = d.day()
        const dayOff = this.dayOffMap[abs]
        out.push({
          abs,
          left: i * this.dayW,
          num: d.date(),
          wd: WD_TH[wd],
          weekend: wd === 0 || wd === 6,
          dayOffClass: dayOff ? dayOff.cssClass : '',
          dayOffTitle: dayOff ? dayOff.names.join(', ') : ''
        })
      }
      return out
    },
    todayLeft () {
      return (0 - this.viewStart) * this.dayW
    },
    todayVisible () {
      return this.viewStart <= 0 && this.viewEnd >= 0
    },
    saleDateDay () {
      return this.salesDate ? this.dateToDay(this.salesDate) : null
    },
    saleDateLeft () {
      return this.saleDateDay !== null ? (this.saleDateDay - this.viewStart) * this.dayW : null
    },
    saleDateVisible () {
      return this.saleDateDay !== null && this.saleDateDay >= this.viewStart && this.saleDateDay <= this.viewEnd
    },
    isDirty () {
      return this.rows.some((row) => this.rowDiff(row))
    }
  },
  async mounted () {
    this.anchor = this.$dayjs().startOf('day')
    this.userRole = getRole()
    await this.fetchData()
    window.addEventListener('keydown', this.onKeydown)
  },
  beforeDestroy () {
    this.removeListeners()
    window.removeEventListener('keydown', this.onKeydown)
  },
  methods: {
    ...mapActions({
      setSnackbar: 'Components/setSnackbar'
    }),
    async fetchData () {
      this.loading = true
      try {
        const [{ data: workflowData }, { data: prototypeData }, dayOffResult] = await Promise.all([
          productionPlanService.getWorkflow(),
          productionPlanService.getPrototypeTasks(this.$route.params.prototypeId),
          productionPlanService.getDayOff()
        ])
        const { data: collectionData } = await productCollectionService.getById(prototypeData?.sketch?.collectionId)

        if (workflowData) {
          this.workflow = workflowData
          this.workflow.stages = (workflowData.stages ?? [])
            .filter((stage) => stage.status === 'active')
            .sort((stageA, stageB) => stageA.order - stageB.order)
        }

        this.prototype = prototypeData ?? null
        this.salesDate = collectionData?.salesDate
        this.collectionName = collectionData?.name

        this.dayOffs = Array.isArray(dayOffResult) ? dayOffResult : (dayOffResult?.data ?? [])
        this.buildRows()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'การดึงข้อมูลผิดพลาด',
          type: 'error'
        })
      } finally {
        this.loading = false
      }
    },
    buildRows () {
      if (!this.workflow || !this.prototype) {
        this.rows = []
        return
      }

      const tasksByStage = Object.fromEntries(
        (this.prototype.tasks ?? []).map((task) => [task.stageId, task])
      )

      this.rows = this.workflow.stages.map((stage) => {
        const task = tasksByStage[stage.id]
        if (!task) {
          return { stage, task: null, locked: true, startDay: 0, duration: 1 }
        }

        const startDay = this.dateToDay(task.startDate)
        const duration = Math.max(1, this.dateToDay(task.dueDate) - startDay + 1)

        return {
          stage,
          task,
          locked: !!task.completedAt || !this.isControllerUser,
          startDay,
          duration
        }
      })
    },
    dateToDay (date) {
      return this.$dayjs(date).startOf('day').diff(this.anchor, 'day')
    },
    dayToDate (day) {
      return this.anchor.add(day, 'day')
    },
    formatDay (day) {
      return this.dayToDate(day).format('DD/MM/YYYY')
    },
    barStyle (row) {
      return {
        left: `${(row.startDay - this.viewStart) * this.dayW }px`,
        width: `${row.duration * this.dayW - 6 }px`
      }
    },
    statusStyleFor (row) {
      const previewTask = {
        ...row.task,
        startDate: this.dayToDate(row.startDay).toISOString(),
        dueDate: this.dayToDate(row.startDay + row.duration - 1).toISOString()
      }
      return calculateTaskStatus(previewTask, TASK_STATUS_STYLES)
    },
    rowDiff (row) {
      if (!row.task || row.locked) return null

      const newStart = this.dayToDate(row.startDay)
      const newDue = this.dayToDate(row.startDay + row.duration - 1)
      const diff = {}

      if (!newStart.isSame(this.$dayjs(row.task.startDate), 'day')) {
        diff.startDate = newStart.toISOString()
      }
      if (!newDue.isSame(this.$dayjs(row.task.dueDate), 'day')) {
        diff.dueDate = newDue.toISOString()
      }

      return Object.keys(diff).length ? diff : null
    },
    isDragging (row) {
      if (!this.drag) return false
      if (this.drag.mode === 'move') {
        return Object.prototype.hasOwnProperty.call(this.drag.origins, row.stage.id)
      }
      return this.drag.rowKey === row.stage.id
    },
    toggleSelect (key) {
      this.selectedKeys = this.selectedKeys.includes(key)
        ? this.selectedKeys.filter((k) => k !== key)
        : [...this.selectedKeys, key]
    },
    onKeydown (e) {
      if (e.key === 'Escape') {
        this.selectedKeys = []
      }
    },
    startMove (e, row) {
      if (row.locked) return

      if (e.metaKey || e.ctrlKey) {
        this.toggleSelect(row.stage.id)
        return
      }

      if (!this.selectedKeys.includes(row.stage.id)) {
        this.selectedKeys = [row.stage.id]
      }

      const origins = {}
      this.selectedKeys.forEach((key) => {
        const r = this.rows.find((rr) => rr.stage.id === key)
        if (r) origins[key] = r.startDay
      })

      this.drag = { mode: 'move', startX: e.clientX, origins }
      this.addListeners()
    },
    startResize (e, row, side) {
      if (row.locked) return
      this.drag = {
        rowKey: row.stage.id, mode: 'resize', side, startX: e.clientX, origStart: row.startDay, origDur: row.duration
      }
      this.addListeners()
    },
    onMove (e) {
      if (!this.drag) return

      const deltaDays = Math.round((e.clientX - this.drag.startX) / this.dayW)
      if (this.compBase === null) this.compBase = this.viewStart

      if (this.drag.mode === 'move') {
        Object.keys(this.drag.origins).forEach((key) => {
          const row = this.rows.find((r) => r.stage.id === key)
          if (row) row.startDay = this.drag.origins[key] + deltaDays
        })
      } else {
        const row = this.rows.find((r) => r.stage.id === this.drag.rowKey)
        if (!row) return
        if (this.drag.side === 'r') {
          row.duration = Math.max(1, this.drag.origDur + deltaDays)
        } else {
          const end = this.drag.origStart + this.drag.origDur
          const newStart = Math.min(this.drag.origStart + deltaDays, end - 1)
          row.startDay = newStart
          row.duration = end - newStart
        }
      }

      this.$nextTick(() => {
        if (this.compBase === null) return
        const after = this.viewStart
        if (this.$refs.scrollEl && after !== this.compBase) {
          this.$refs.scrollEl.scrollLeft += (this.compBase - after) * this.dayW
        }
        this.compBase = null
      })
    },
    onUp () {
      this.drag = null
      this.compBase = null
      this.removeListeners()
    },
    addListeners () {
      window.addEventListener('pointermove', this.onMove)
      window.addEventListener('pointerup', this.onUp)
    },
    removeListeners () {
      window.removeEventListener('pointermove', this.onMove)
      window.removeEventListener('pointerup', this.onUp)
    },
    openTaskHistory (row) {
      this.historyTaskId = row.task.id
      this.historyTitle = `ประวัติ ${row.stage.name}`
      this.historyDrawer = true
    },
    openPrototypeHistory () {
      this.historyTaskId = null
      this.historyTitle = `ประวัติ ${this.prototype.sketch.sketchId}`
      this.historyDrawer = true
    },
    lastChangedText (task) {
      if (!task.changeDateBy) return 'ดูประวัติ'
      const changedAt = task.changeDateAt ? this.$dayjs(task.changeDateAt).format('DD/MM/YYYY HH:mm') : ''
      return `แก้วันที่ล่าสุดโดย ${task.changeDateBy.email} ${changedAt}`.trim()
    },
    openSaveDialog () {
      this.note = ''
      this.noteDialog = true
    },
    async confirmSave () {
      if (!this.note) return

      const tasks = this.rows
        .map((row) => {
          const diff = this.rowDiff(row)
          return diff ? { id: row.task.id, note: this.note, ...diff } : null
        })
        .filter(Boolean)

      if (!tasks.length) {
        this.noteDialog = false
        return
      }

      this.saving = true
      try {
        const { data } = await productionPlanService.batchUpdateTaskDate({ tasks })
        this.setSnackbar({
          value: true,
          message: `บันทึกสำเร็จ (${data?.updatedCount ?? tasks.length} tasks)`,
          type: 'success'
        })
        this.noteDialog = false
        this.selectedKeys = []
        await this.fetchData()
      } catch (error) {
        this.setSnackbar({
          value: true,
          message: error?.message ?? 'บันทึกไม่สำเร็จ',
          type: 'error'
        })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.chart,
.chart *,
.chart *::before,
.chart *::after {
  box-sizing: border-box;
}
.chart {
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  overflow: hidden;
}
.grid {
  display: grid;
  grid-template-columns: 260px 1fr;
}
.names {
  border-right: 1px solid #e0e0e0;
}
.names .head {
  height: 68px;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid #e0e0e0;
}
.head-month-label {
  height: 28px;
  flex: 0 0 28px;
  border-bottom: 1px solid #e0e0e0;
}
.head-stage-label {
  height: 40px;
  flex: 0 0 40px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  font-size: 11px;
  font-weight: 600;
  color: #6b7686;
  text-transform: uppercase;
  letter-spacing: .06em;
}
.names .cell {
  height: 52px;
  display: flex;
  align-items: center;
  padding: 0 8px 0 14px;
  border-bottom: 1px solid #e0e0e0;
}
.names .cell-text {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
}
.names .cell .t {
  font-size: 13.5px;
  font-weight: 600;
}
.names .cell .d {
  font-size: 11px;
  color: #6b7686;
  margin-top: 2px;
}
.scroll {
  overflow-x: auto;
  overflow-y: hidden;
}
.timeline {
  position: relative;
}
.months-row {
  position: relative;
  height: 28px;
  border-bottom: 1px solid #e0e0e0;
}
.month-cell {
  position: absolute;
  top: 0;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  color: #1c2433;
  letter-spacing: .04em;
  border-left: 2px solid #bdbdbd;
  overflow: hidden;
  white-space: nowrap;
}
.month-cell:first-child {
  border-left: none;
}
.days {
  display: flex;
  height: 40px;
  border-bottom: 1px solid #e0e0e0;
}
.day {
  width: 44px;
  flex: 0 0 44px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  color: #6b7686;
  border-left: 1px solid #e0e0e0;
}
.day .num {
  font-size: 12.5px;
  font-weight: 600;
  color: #1c2433;
}
.day.weekend {
  background: #eceff1;
}
.rows {
  position: relative;
}
.colbg {
  position: absolute;
  inset: 0;
  z-index: 0;
}
.colbg .c {
  position: absolute;
  top: 0;
  bottom: 0;
  border-left: 1px solid #e0e0e0;
}
.colbg .c.weekend {
  background: #eceff1;
}
.today-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(47, 111, 107, 0.55);
  pointer-events: none;
  z-index: 2;
}
.today-flag {
  position: absolute;
  top: 0;
  left: 2px;
  background: #2f6f6b;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 7px 2px 5px;
  border-radius: 0 3px 3px 0;
  white-space: nowrap;
  line-height: 1.6;
}
.saleDate-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: rgba(211, 47, 47, 0.55);
  pointer-events: none;
  z-index: 2;
}
.saleDate-flag {
  position: absolute;
  top: 0;
  left: 2px;
  background: #d32f2f;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  padding: 2px 7px 2px 5px;
  border-radius: 0 3px 3px 0;
  white-space: nowrap;
  line-height: 1.6;
}
.gantt-row {
  height: 52px;
  border-bottom: 1px solid #cfd3d6;
  position: relative;
  z-index: 1;
}
.bar {
  position: absolute;
  top: 9px;
  height: 34px;
  border-radius: 8px;
  z-index: 3;
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  padding: 0 10px;
  cursor: grab;
  user-select: none;
  box-shadow: 0 1px 2px rgba(20, 30, 50, .18);
  touch-action: none;
  overflow: hidden;
  white-space: nowrap;
}
.bar--locked {
  cursor: default;
}
.bar--selected {
  outline: 2px solid #1976d2;
  outline-offset: 1px;
}
.bar:active {
  cursor: grabbing;
}
.bar.dragging {
  box-shadow: 0 4px 12px rgba(20, 30, 50, .28);
  z-index: 5;
}
.bar .txt {
  overflow: hidden;
  text-overflow: ellipsis;
  pointer-events: none;
}
.handle {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 12px;
  cursor: ew-resize;
  touch-action: none;
}
.handle.l {
  left: 0;
}
.handle.r {
  right: 0;
}
.hint {
  font-size: 11.5px;
  color: #6b7686;
  margin-top: 12px;
  line-height: 1.7;
}
.day.dayoff-th,
.colbg .c.dayoff-th {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 193, 7, 0.13) 0,
    rgba(255, 193, 7, 0.13) 5px,
    transparent 5px,
    transparent 10px
  );
}
.day.dayoff-cn,
.colbg .c.dayoff-cn {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(244, 67, 54, 0.13) 0,
    rgba(244, 67, 54, 0.13) 5px,
    transparent 5px,
    transparent 10px
  );
}
.day.dayoff-both,
.colbg .c.dayoff-both {
  background-image: repeating-linear-gradient(
    45deg,
    rgba(156, 39, 176, 0.13) 0,
    rgba(156, 39, 176, 0.13) 5px,
    transparent 5px,
    transparent 10px
  );
}
.legend-swatch {
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 3px;
  vertical-align: -2px;
  margin-right: 2px;
}
.legend-th {
  background-color: #fff8e1;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(255, 193, 7, 0.28) 0,
    rgba(255, 193, 7, 0.28) 3px,
    transparent 3px,
    transparent 6px
  );
}
.legend-cn {
  background-color: #ffebee;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(244, 67, 54, 0.28) 0,
    rgba(244, 67, 54, 0.28) 3px,
    transparent 3px,
    transparent 6px
  );
}
.legend-both {
  background-color: #f3e5f5;
  background-image: repeating-linear-gradient(
    45deg,
    rgba(156, 39, 176, 0.28) 0,
    rgba(156, 39, 176, 0.28) 3px,
    transparent 3px,
    transparent 6px
  );
}
</style>
